// src/utils/axios.js
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_API_URL || 'https://swanpointb.onrender.com';
const TIMEOUT_DURATION = 15000; // 15 seconds


const getBaseURL = () => {
  return localStorage.getItem('apiBaseUrl') || 'https://swanpointb.onrender.com';
};

const axiosInstance = axios.create({
  timeout: 30000,
  headers: {
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  }
});

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    // Get the latest base URL
    config.baseURL = getBaseURL();
    
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Log request in development
    if (process.env.NODE_ENV === 'development') {
      console.log('Request:', {
        url: `${config.baseURL}${config.url}`,
        method: config.method,
        data: config.data instanceof FormData ? 'FormData' : config.data
      });
    }

    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response) {
      // Network error
      localStorage.removeItem('apiBaseUrl'); // Clear stored URL on network error
      return Promise.reject({
        message: 'Network error. Please check your connection.',
        isNetworkError: true
      });
    }
    return Promise.reject(error);
  }
);

// Health check method
axiosInstance.checkHealth = async () => {
  const urls = [
    'https://swanpointb.onrender.com/api/test',
    'http://localhost:5000/api/test'
  ];

  for (const url of urls) {
    try {
      const response = await axios.get(url, { timeout: 5000 });
      if (response.status === 200) {
        localStorage.setItem('apiBaseUrl', url.split('/api')[0]);
        return { isHealthy: true, baseUrl: url.split('/api')[0] };
      }
    } catch (err) {
      console.warn(`Health check failed for ${url}:`, err.message);
    }
  }
  
  return { isHealthy: false, error: 'No servers available' };
};

// Request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Request failed:', error);
    if (error.response?.status === 404) {
      console.error('Resource not found:', error.config.url);
    }
    return Promise.reject(error);
  }
);

// Single Request Interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    
    // Set Authorization header if token exists
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Set content type based on data type
    if (config.data instanceof FormData) {
      config.headers['Content-Type'] = 'multipart/form-data';
    } else {
      config.headers['Content-Type'] = 'application/json';
    }
    
    config.headers['Accept'] = 'application/json';

    // Special handling for KYC endpoints
    if (config.url?.includes('/kyc')) {
      config.timeout = 300000; // 5 minutes for KYC
      config.headers['X-Request-Type'] = 'KYC';
    }

    // Debug logging
    if (process.env.NODE_ENV === 'development') {
      console.log('🚀 Request:', {
        url: `${config.baseURL}${config.url}`,
        method: config.method?.toUpperCase(),
        headers: { ...config.headers },
        data: config.data instanceof FormData ? 'FormData: File upload' : config.data
      });
    }

    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Single Response Interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('✅ Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data
      });
    }
    return response;
  },
  async (error) => {
    // Prepare error response
    const errorResponse = {
      message: 'An error occurred',
      status: null,
      data: null,
      type: 'unknown'
    };

    // Handle timeout errors
    if (error.code === 'ECONNABORTED') {
      errorResponse.type = 'timeout';
      errorResponse.message = 'Request timed out. Please try again.';
      return Promise.reject(errorResponse);
    }

    // Handle network errors
    if (!error.response) {
      errorResponse.type = 'network';
      errorResponse.message = 'Network error. Please check your connection.';
      return Promise.reject(errorResponse);
    }

    // Handle server responses with error status
    const { response } = error;
    errorResponse.status = response.status;
    errorResponse.data = response.data;

    switch (response.status) {
      case 401:
        errorResponse.type = 'auth';
        errorResponse.message = 'Authentication required. Please login again.';
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
        break;
      case 403:
        errorResponse.type = 'permission';
        errorResponse.message = 'Access denied.';
        break;
      case 404:
        errorResponse.type = 'not_found';
        errorResponse.message = 'Resource not found.';
        break;
      case 500:
        errorResponse.type = 'server';
        errorResponse.message = 'Server error. Please try again later.';
        break;
      default:
        errorResponse.message = response.data?.message || 'An unexpected error occurred';
    }

    if (process.env.NODE_ENV === 'development') {
      console.error('❌ Error:', errorResponse);
    }

    return Promise.reject(errorResponse);
  }
);

// Health check method
axiosInstance.checkHealth = async () => {
  try {
    const response = await axiosInstance.get('/test', {
      timeout: 5000 // Short timeout for health check
    });
    return {
      isHealthy: true,
      data: response.data
    };
  } catch (error) {
    console.error('Health check failed:', error);
    return {
      isHealthy: false,
      error: error.message
    };
  }
};

// Helper method for retrying failed requests
axiosInstance.retryRequest = async (config, retries = 3, delay = 1000) => {
  try {
    return await axiosInstance(config);
  } catch (error) {
    if (retries === 0 || config.data instanceof FormData) throw error;
    await new Promise(resolve => setTimeout(resolve, delay));
    return axiosInstance.retryRequest(config, retries - 1, delay * 2);
  }
};

export default axiosInstance;